import React, { useState } from 'react';
import { Copy, Check, Search } from 'lucide-react';

const SAMPLE_URL =
  'https://staging.ecomm365.com/api/TransactionLookupAPI/api/MerchantTransactionLookup/GetTransactionLookup?transactionId={TRANSACTION_ID}';

const SAMPLE_CURL = `curl --request GET \\
  --url "https://staging.ecomm365.com/api/TransactionLookupAPI/api/MerchantTransactionLookup/GetTransactionLookup?transactionId=066dd1ad-4441-44b6-1e39-c6a4b5e2c000" \\
  --header "Authorization: Basic {BASE64(username:password)}" \\
  --header "Accept: application/json"`;

/**
 * Transaction Lookup — Request Reference panel. Read-only sample call shown
 * alongside the docs. Previously this was an interactive "Try It Live" form.
 */
const TransactionLookupPlayground = () => {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);

  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="relative rounded-xl overflow-hidden border border-[#1F4E79]/20 shadow-lg bg-white my-6"
      data-testid="lookup-playground"
    >
      <div className="flex items-center justify-between gap-3 px-6 py-4 bg-[#1F4E79] text-white">
        <div className="flex items-center gap-3 min-w-0">
          <Search size={20} className="text-[#ffcba6] flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">Request Reference — Transaction Lookup</h3>
            <p className="text-xs text-white/80 mt-0.5 line-clamp-2">
              GET request structure for looking up a transaction by ID using Basic Auth.
            </p>
          </div>
        </div>
        <span className="hidden sm:inline-flex items-center px-2 py-1 rounded bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[10px] font-bold uppercase tracking-wider">
          Example
        </span>
      </div>

      <div className="p-5 sm:p-6 space-y-5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Method</span>
          <code className="px-2.5 py-1 rounded bg-gray-100 border border-gray-200 text-[13px] font-mono text-[#1F4E79]">GET</code>
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide ml-3">Auth</span>
          <code className="px-2.5 py-1 rounded bg-gray-100 border border-gray-200 text-[13px] font-mono text-[#1F4E79]">Basic Auth</code>
        </div>

        {/* URL */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Endpoint URL</span>
            <button
              type="button"
              onClick={() => copy(SAMPLE_URL, setCopiedUrl)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F4E79] hover:text-[#163a5f] transition-colors"
              data-testid="lookup-copy-url"
            >
              {copiedUrl ? (
                <>
                  <Check size={14} /> Copied
                </>
              ) : (
                <>
                  <Copy size={14} /> Copy URL
                </>
              )}
            </button>
          </div>
          <pre className="overflow-x-auto p-4 text-xs leading-relaxed bg-[#1e1e1e] text-gray-100 font-mono whitespace-pre rounded-md border border-gray-800">
            {SAMPLE_URL}
          </pre>
        </div>

        {/* cURL */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Sample Request (cURL)</span>
            <button
              type="button"
              onClick={() => copy(SAMPLE_CURL, setCopiedCurl)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F4E79] hover:text-[#163a5f] transition-colors"
              data-testid="lookup-copy-curl"
            >
              {copiedCurl ? (
                <>
                  <Check size={14} /> Copied
                </>
              ) : (
                <>
                  <Copy size={14} /> Copy cURL
                </>
              )}
            </button>
          </div>
          <pre className="overflow-x-auto p-4 text-xs leading-relaxed bg-[#1e1e1e] text-gray-100 font-mono whitespace-pre rounded-md border border-gray-800">
            {SAMPLE_CURL}
          </pre>
        </div>

        <p className="text-[12px] text-gray-500 leading-relaxed">
          Sample shown for reference. Contact our integration team to discuss sandbox access for your build.
        </p>
      </div>
    </div>
  );
};

export default TransactionLookupPlayground;
